import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

// A "print queue" of warehouse items, keyed by item id, each with a label
// quantity - shaped like the existing selection patterns in
// ServiceResources.jsx / ResourcesManager.jsx (Map<id, {item, quantity}>).
// Persisted to sessionStorage (not localStorage) so a stale queue doesn't
// survive across days, but does survive navigating between the items table
// and an individual item's page - this provider is mounted above <Routes>
// in DefaultContainer.js specifically so that works.
const STORAGE_KEY = "warehouseLabelBuffer";

const WarehouseLabelBufferContext = createContext(null);

function loadInitial() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function WarehouseLabelBufferProvider({ children }) {
  const [buffer, setBuffer] = useState(loadInitial);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(buffer));
  }, [buffer]);

  const isBuffered = (itemId) => Boolean(buffer[itemId]);

  const toggle = (item) => {
    setBuffer((prev) => {
      if (prev[item.id]) {
        const next = { ...prev };
        delete next[item.id];
        return next;
      }
      return { ...prev, [item.id]: { item, quantity: 1 } };
    });
  };

  const remove = (itemId) => {
    setBuffer((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  };

  const setQuantity = (itemId, quantity) => {
    setBuffer((prev) => {
      if (!prev[itemId]) return prev;
      if (quantity <= 0) {
        const next = { ...prev };
        delete next[itemId];
        return next;
      }
      return { ...prev, [itemId]: { ...prev[itemId], quantity } };
    });
  };

  const clear = () => setBuffer({});

  // Items are clinic-scoped; a master browsing a different clinic than
  // whatever's currently buffered would otherwise be able to build a
  // cross-clinic label batch. Called by the warehouse pages on mount.
  const pruneToClinicId = (clinicId) => {
    setBuffer((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(
          ([, entry]) => entry.item.clinicId === clinicId,
        ),
      );
      return Object.keys(next).length === Object.keys(prev).length
        ? prev
        : next;
    });
  };

  const entries = useMemo(() => Object.values(buffer), [buffer]);

  const value = {
    entries,
    count: entries.length,
    isBuffered,
    toggle,
    remove,
    setQuantity,
    clear,
    pruneToClinicId,
  };

  return (
    <WarehouseLabelBufferContext.Provider value={value}>
      {children}
    </WarehouseLabelBufferContext.Provider>
  );
}

export function useWarehouseLabelBuffer() {
  const context = useContext(WarehouseLabelBufferContext);
  if (!context) {
    throw new Error(
      "useWarehouseLabelBuffer must be used within a WarehouseLabelBufferProvider",
    );
  }
  return context;
}
